import { useState, useEffect, useRef } from "react";
import { askClaude } from "./utils/api";
import { buildExplainPrompt, buildQuizExplainPrompt, buildInterviewSystem } from "./utils/prompts";
import { S } from "./utils/styles";
import { TOPICS, ROLES, COMPANIES } from "./data/constants";
import { NOTES } from "./data/notes";
import { QUIZ } from "./data/quiz";
import AIPanel from "./components/AIPanel";
import InterviewPanel from "./components/InterviewPanel";
import CostPanel from "./components/CostPanel";
import LoginScreen, { getStoredKey, clearStoredKey } from "./components/LoginScreen";

export default function App() {
  const [apiKey, setApiKey]               = useState(() => getStoredKey());
  const [mode, setMode]                   = useState("home");
  const [activeTopic, setActiveTopic]     = useState("supervised");
  const [activeRole, setActiveRole]       = useState("engineer");
  const [activeCompany, setActiveCompany] = useState("Wells Fargo GCC");
  const [progress, setProgress]           = useState({});
  const [searchQuery, setSearchQuery]     = useState("");
  const [openNote, setOpenNote]           = useState(null);
  const [noteTab, setNoteTab]             = useState("notes");
  const [aiPanel, setAiPanel]             = useState(null);
  const [showInterview, setShowInterview] = useState(false);
  const [showCosts,     setShowCosts]     = useState(false);

  // Quiz state
  const [quizQs, setQuizQs]   = useState([]);
  const [qIdx, setQIdx]       = useState(0);
  const [qSel, setQSel]       = useState(null);
  const [qConf, setQConf]     = useState(false);
  const [qAnswers, setQAnswers] = useState([]);
  const [qScore, setQScore]   = useState(0);
  const [qDone, setQDone]     = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [timerOn, setTimerOn] = useState(false);

  // Flashcard state
  const [fcIdx, setFcIdx]       = useState(0);
  const [fcFlipped, setFcFlipped] = useState(false);

  const topic    = TOPICS.find(t => t.id === activeTopic);
  const color    = topic?.color || "#38BDF8";
  const roleName = ROLES.find(r => r.id === activeRole)?.label || "ML Engineer";
  const key      = `${activeTopic}_${activeRole}`;
  const prog     = progress[key] || { studied: new Set(), scores: [] };

  // Timer
  useEffect(() => {
    let iv;
    if (timerOn && timeLeft > 0 && !qConf)
      iv = setInterval(() => setTimeLeft(t => t - 1), 1000);
    else if (timeLeft === 0 && !qConf && timerOn)
      confirmQ(true);
    return () => clearInterval(iv);
  }, [timerOn, timeLeft, qConf]);

  const markStudied = id =>
    setProgress(prev => {
      const cur  = prev[key] || { studied: new Set(), scores: [] };
      const next = new Set(cur.studied);
      next.add(id);
      return { ...prev, [key]: { ...cur, studied: next } };
    });

  const startQuiz = () => {
    const qs = [...(QUIZ[activeTopic]?.[activeRole] || [])];
    for (let i = qs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [qs[i], qs[j]] = [qs[j], qs[i]];
    }
    setQuizQs(qs); setQIdx(0); setQSel(null); setQConf(false);
    setQAnswers([]); setQScore(0); setQDone(false);
    setTimeLeft(30); setTimerOn(true);
    setMode("quiz");
  };

  const confirmQ = (timeout = false) => {
    if (qConf) return;
    setQConf(true); setTimerOn(false);
    const ans = timeout ? -1 : qSel;
    const ok  = ans === quizQs[qIdx]?.a;
    setQAnswers(prev => [...prev, { selected: ans, correct: ok, timeout }]);
    if (ok) setQScore(s => s + 1);
  };

  const nextQ = () => {
    if (qIdx + 1 >= quizQs.length) {
      const pct = Math.round((qScore / quizQs.length) * 100);
      setProgress(prev => {
        const cur = prev[key] || { studied: new Set(), scores: [] };
        return { ...prev, [key]: { ...cur, scores: [...cur.scores, pct] } };
      });
      setQDone(true); return;
    }
    setQIdx(i => i + 1); setQSel(null); setQConf(false);
    setTimeLeft(30); setTimerOn(true);
  };

  // ── LOGIN GATE ──
  if (!apiKey) return <LoginScreen onLogin={setApiKey} />;

  // ── HOME ──
  if (mode === "home") return (
    <div style={S.app}>
      {aiPanel && <AIPanel {...aiPanel} color={color} apiKey={apiKey} onClose={() => setAiPanel(null)} />}
      {showInterview && <InterviewPanel role={roleName} company={activeCompany} topic={topic?.label} topicColor={color} apiKey={apiKey} onClose={() => setShowInterview(false)} />}
      {showCosts     && <CostPanel apiKey={apiKey} onClose={() => setShowCosts(false)} />}

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "24px 18px" }}>
        <div style={{ textAlign: "center", paddingBottom: 36 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#0d0e1a", border:"1px solid #1e2030", borderRadius:99, padding:"5px 16px", marginBottom:18, fontSize:11, letterSpacing:3, color:"#64748b", textTransform:"uppercase" }}>
            <span style={{ width:6, height:6, borderRadius:99, background:"#34D399" }} /> AI Architect Prep
          </div>
          <h1 style={{ fontSize:"clamp(26px,5vw,46px)", fontWeight:900, margin:"0 0 10px", letterSpacing:-2, background:"linear-gradient(135deg,#e2e8f0 40%,#38BDF8)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
            ML Interview Prep Hub
          </h1>
          <p style={{ color:"#475569", fontSize:14, maxWidth:440, margin:"0 auto 24px" }}>
            Study → Flashcards → Quiz → AI Interview.<br />
            <span style={{ color:"#34D399", fontSize:12 }}>✓ Powered by Claude Haiku on AWS Bedrock</span>
          </p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, maxWidth:700, margin:"0 auto" }}>
            {[
              { icon:"📚", label:"Study Notes",  desc:"33 concepts with code, analogies, architect view + Ask Claude", action: () => setMode("notes"),     c:"#38BDF8" },
              { icon:"🃏", label:"Flashcards",   desc:"Quick flip-card review — Ask Claude on any card",               action: () => { setFcIdx(0); setFcFlipped(false); setMode("flashcard"); }, c:"#FB923C" },
              { icon:"🎯", label:"Quiz Mode",    desc:"Timed MCQs — Ask Claude to explain any answer",                 action: startQuiz,                   c:"#C084FC" },
              { icon:"🎤", label:"AI Interview", desc:"Full mock interview with scoring via Bedrock Claude",           action: () => setShowInterview(true), c:"#34D399" },
              { icon:"💰", label:"AWS Costs",    desc:"Live month-to-date spend by service via Cost Explorer",      action: () => setShowCosts(true),     c:"#FBBF24" },
            ].map(m => (
              <button key={m.label} onClick={m.action} style={{ ...S.card(false, m.c), padding:"18px 16px", textAlign:"left", display:"flex", gap:12, alignItems:"flex-start" }}>
                <span style={{ fontSize:24 }}>{m.icon}</span>
                <div>
                  <div style={{ fontWeight:800, fontSize:14, color:m.c, marginBottom:4 }}>{m.label}</div>
                  <div style={{ fontSize:11, color:"#475569", lineHeight:1.4 }}>{m.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Topic + Role + Company */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:18 }}>
          <div>
            <div style={{ fontSize:11, letterSpacing:3, color:"#475569", marginBottom:8, textTransform:"uppercase" }}>Topic</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:7 }}>
              {TOPICS.map(t => (
                <button key={t.id} onClick={() => setActiveTopic(t.id)} style={{ ...S.card(activeTopic === t.id, t.color), padding:"11px 8px", textAlign:"center" }}>
                  <div style={{ fontSize:18, marginBottom:3 }}>{t.icon}</div>
                  <div style={{ fontSize:10, fontWeight:700, color: activeTopic === t.id ? t.color : "#64748b" }}>{t.label}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize:11, letterSpacing:3, color:"#475569", marginBottom:8, textTransform:"uppercase" }}>Role</div>
            <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
              {ROLES.map(r => (
                <button key={r.id} onClick={() => setActiveRole(r.id)} style={{ ...S.card(activeRole === r.id, color), padding:"10px 12px", display:"flex", alignItems:"center", gap:8, textAlign:"left" }}>
                  <span style={{ fontSize:16 }}>{r.icon}</span>
                  <span style={{ fontSize:12, fontWeight:700, color: activeRole === r.id ? color : "#64748b" }}>{r.label}</span>
                </button>
              ))}
            </div>
            <select value={activeCompany} onChange={e => setActiveCompany(e.target.value)} style={{ width:"100%", background:"#0d0e1a", border:"1px solid #1e2030", borderRadius:8, padding:"9px 11px", color:"#94a3b8", fontFamily:"inherit", fontSize:12, outline:"none", marginTop:7 }}>
              {COMPANIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* Progress */}
        <div style={{ background:"#0d0e1a", border:"1px solid #151728", borderRadius:14, padding:"16px 18px" }}>
          <div style={{ fontSize:10, letterSpacing:3, color:"#475569", marginBottom:12, textTransform:"uppercase" }}>
            Progress — {topic?.label} / {roleName}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
            {[
              { label:"Notes Studied", value:`${prog.studied.size}/${NOTES[activeTopic]?.length || 0}`, c:"#38BDF8" },
              { label:"Quiz Attempts", value: prog.scores.length,                                        c:"#C084FC" },
              { label:"Best Score",    value: prog.scores.length ? Math.max(...prog.scores) + "%" : "—", c:"#34D399" },
            ].map(s => (
              <div key={s.label} style={{ textAlign:"center" }}>
                <div style={{ fontSize:22, fontWeight:900, color:s.c }}>{s.value}</div>
                <div style={{ fontSize:10, color:"#475569", marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ── NOTES ──
  if (mode === "notes") {
    const notes = (NOTES[activeTopic] || []).filter(n =>
      !searchQuery ||
      n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      n.summary.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return (
      <div style={S.app}>
        {aiPanel && <AIPanel {...aiPanel} color={color} apiKey={apiKey} onClose={() => setAiPanel(null)} />}
        {showInterview && <InterviewPanel role={roleName} company={activeCompany} topic={topic?.label} topicColor={color} apiKey={apiKey} onClose={() => setShowInterview(false)} />}
        <div style={S.nav}>
          <button onClick={() => setMode("home")} style={{ ...S.btn(color, true), padding:"5px 11px", fontSize:11 }}>← Home</button>
          <span style={S.tag(color)}>{topic?.icon} {topic?.label}</span>
          <span style={S.tag("#64748b")}>{ROLES.find(r => r.id === activeRole)?.icon} {roleName}</span>
          <div style={{ flex:1 }} />
          <button onClick={() => setShowInterview(true)} style={{ ...S.btn("#34D399", true), padding:"5px 11px", fontSize:11 }}>🎤 Interview</button>
          <button onClick={() => { setFcIdx(0); setFcFlipped(false); setMode("flashcard"); }} style={{ ...S.btn("#FB923C", true), padding:"5px 11px", fontSize:11 }}>🃏 Cards</button>
          <button onClick={startQuiz} style={{ ...S.btn(color, false), padding:"5px 11px", fontSize:11 }}>🎯 Quiz</button>
        </div>
        <div style={{ maxWidth:760, margin:"0 auto", padding:"20px 18px" }}>
          <div style={{ position:"relative", marginBottom:16 }}>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search concepts..."
              style={{ width:"100%", background:"#0d0e1a", border:`1px solid ${color}44`, borderRadius:10, padding:"9px 14px 9px 34px", color:"#e2e8f0", fontFamily:"inherit", fontSize:13, outline:"none", boxSizing:"border-box" }} />
            <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)" }}>🔍</span>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
            {notes.map(note => {
              const studied = prog.studied.has(note.id);
              const open    = openNote === note.id;
              return (
                <div key={note.id} style={{ ...S.card(open || studied, color), overflow:"hidden" }}>
                  <button onClick={() => { setOpenNote(open ? null : note.id); setNoteTab("notes"); if (!studied) markStudied(note.id); }}
                    style={{ width:"100%", padding:"13px 16px", background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:10, textAlign:"left", color:"#e2e8f0" }}>
                    <span style={{ fontSize:18 }}>{note.emoji}</span>
                    <span style={{ flex:1, fontWeight:700, fontSize:13 }}>{note.title}</span>
                    {studied && <span style={{ fontSize:10, color:"#34D399" }}>✓</span>}
                    <span style={{ color:"#475569", fontSize:11 }}>{open ? "▲" : "▼"}</span>
                  </button>
                  {open && (
                    <div style={{ padding:"0 16px 16px" }}>
                      <div style={{ background:`${color}12`, border:`1px solid ${color}22`, borderRadius:8, padding:"10px 13px", marginBottom:11, fontSize:13, color:"#94a3b8", lineHeight:1.6 }}>{note.summary}</div>
                      <div style={{ display:"flex", gap:5, marginBottom:11, flexWrap:"wrap" }}>
                        {["notes", "analogy", "code", "architect"].map(t => (
                          <button key={t} onClick={() => setNoteTab(t)} style={{ ...S.btn(color, noteTab !== t), padding:"4px 10px", fontSize:10, borderRadius:6, textTransform:"capitalize" }}>
                            {t === "notes" ? "📝" : t === "analogy" ? "💡" : t === "code" ? "💻" : "🏗️"} {t === "architect" ? "Architect" : t}
                          </button>
                        ))}
                        <button onClick={() => setAiPanel({ title:`Ask Claude: ${note.title}`, prompt: buildExplainPrompt(note, roleName) })}
                          style={{ background:"#34D39922", border:"1px solid #34D39955", borderRadius:6, padding:"4px 11px", fontSize:10, color:"#34D399", cursor:"pointer", fontWeight:700, fontFamily:"inherit" }}>
                          🤖 Ask Claude
                        </button>
                      </div>
                      {noteTab === "notes"    && <ul style={{ margin:0, padding:"0 0 0 16px", display:"flex", flexDirection:"column", gap:5 }}>{note.keyPoints.map((p, j) => <li key={j} style={{ color:"#94a3b8", fontSize:12, lineHeight:1.5 }}>{p}</li>)}</ul>}
                      {noteTab === "analogy"  && <div style={{ background:"#0a1020", border:"1px solid #1e3040", borderRadius:8, padding:"11px 13px", color:"#94a3b8", fontSize:13, lineHeight:1.7 }}>💡 {note.analogy}</div>}
                      {noteTab === "code"     && <pre style={{ background:"#060810", border:"1px solid #151728", borderRadius:8, padding:13, margin:0, fontSize:11, color:"#86efac", overflowX:"auto", lineHeight:1.6 }}><code>{note.code}</code></pre>}
                      {noteTab === "architect" && <div style={{ background:"#0a150a", border:"1px solid #34D39933", borderRadius:8, padding:"11px 13px" }}><div style={{ fontSize:10, color:"#34D399", marginBottom:5, letterSpacing:1 }}>🏗️ ARCHITECT VIEW</div><div style={{ color:"#94a3b8", fontSize:12, lineHeight:1.6 }}>{note.architectNote}</div></div>}
                      <div style={{ marginTop:9, padding:"6px 10px", background:"#0d0e1a", borderRadius:6, fontSize:10, color:"#475569" }}>💡 Quiz hint: {note.quizHint}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── FLASHCARDS ──
  if (mode === "flashcard") {
    const cards = NOTES[activeTopic] || [];
    const card  = cards[fcIdx];
    if (!card) return null;
    return (
      <div style={S.app}>
        {aiPanel && <AIPanel {...aiPanel} color={color} apiKey={apiKey} onClose={() => setAiPanel(null)} />}
        <div style={S.nav}>
          <button onClick={() => setMode("home")} style={{ ...S.btn(color, true), padding:"5px 11px", fontSize:11 }}>← Home</button>
          <span style={S.tag(color)}>{topic?.icon} {topic?.label}</span>
          <div style={{ flex:1 }} />
          <span style={{ color:"#64748b", fontSize:12 }}>{fcIdx + 1}/{cards.length}</span>
          <button onClick={startQuiz} style={{ ...S.btn(color, false), padding:"5px 11px", fontSize:11 }}>🎯 Quiz</button>
        </div>
        <div style={{ maxWidth:560, margin:"0 auto", padding:"32px 18px", display:"flex", flexDirection:"column", alignItems:"center" }}>
          <div style={{ width:"100%", background:"#0d0e1a", borderRadius:4, height:4, overflow:"hidden", marginBottom:20 }}>
            <div style={{ height:"100%", width:`${((fcIdx + 1) / cards.length) * 100}%`, background:color, transition:"width 0.3s" }} />
          </div>
          <div onClick={() => setFcFlipped(!fcFlipped)} style={{ width:"100%", minHeight:220, background:"#0d0e1a", border:`2px solid ${fcFlipped ? color : "#1e2030"}`, borderRadius:18, padding:26, cursor:"pointer", transition:"all 0.25s", boxShadow: fcFlipped ? `0 0 24px ${color}22` : "none", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center" }}>
            {!fcFlipped ? (
              <><div style={{ fontSize:36, marginBottom:12 }}>{card.emoji}</div><h2 style={{ fontSize:20, fontWeight:900, marginBottom:6, color }}>{card.title}</h2><p style={{ color:"#475569", fontSize:12, margin:0 }}>Tap to reveal</p></>
            ) : (
              <><div style={{ fontSize:12, color:"#94a3b8", lineHeight:1.7, marginBottom:10, textAlign:"left", width:"100%" }}>{card.summary}</div><div style={{ width:"100%", marginBottom:8 }}>{card.keyPoints.slice(0, 3).map((p, i) => <div key={i} style={{ fontSize:11, color:"#64748b", marginBottom:3 }}>• {p}</div>)}</div><div style={{ width:"100%", background:"#060710", borderRadius:5, padding:"7px 9px", fontSize:10, color:"#475569", textAlign:"left" }}>🏗️ {card.architectNote.slice(0, 100)}...</div></>
            )}
          </div>
          <button onClick={() => setAiPanel({ title:`Ask Claude: ${card.title}`, prompt: buildExplainPrompt(card, roleName) })}
            style={{ marginTop:10, background:"#34D39922", border:"1px solid #34D39955", borderRadius:8, padding:"8px 18px", color:"#34D399", cursor:"pointer", fontWeight:700, fontSize:12, fontFamily:"inherit" }}>
            🤖 Ask Claude to explain
          </button>
          <div style={{ display:"flex", gap:8, marginTop:10 }}>
            <button onClick={() => { if (fcIdx > 0) { setFcIdx(i => i - 1); setFcFlipped(false); } }} disabled={fcIdx === 0} style={{ ...S.btn(color, true), padding:"9px 18px", opacity: fcIdx === 0 ? 0.3 : 1 }}>←</button>
            <button onClick={() => setFcFlipped(!fcFlipped)} style={{ ...S.btn(color, true), padding:"9px 18px" }}>{fcFlipped ? "Hide" : "Reveal"}</button>
            <button onClick={() => { if (fcIdx < cards.length - 1) { setFcIdx(i => i + 1); setFcFlipped(false); } else setMode("home"); }} style={{ ...S.btn(color, false), padding:"9px 18px" }}>
              {fcIdx < cards.length - 1 ? "→" : "Done"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── QUIZ ──
  if (mode === "quiz") {
    if (qDone) {
      const pct = Math.round((qScore / quizQs.length) * 100);
      const gc  = pct >= 80 ? "#34D399" : pct >= 60 ? "#FB923C" : "#F87171";
      const grd = pct >= 90 ? "S" : pct >= 80 ? "A" : pct >= 70 ? "B" : pct >= 60 ? "C" : "F";
      return (
        <div style={{ ...S.app, display:"flex", alignItems:"center", justifyContent:"center" }}>
          {aiPanel && <AIPanel {...aiPanel} color={color} apiKey={apiKey} onClose={() => setAiPanel(null)} />}}
          <div style={{ maxWidth:440, width:"100%", padding:22, textAlign:"center" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>{pct >= 80 ? "🏆" : pct >= 60 ? "📊" : "📚"}</div>
            <h1 style={{ fontSize:26, fontWeight:900, marginBottom:18 }}>Quiz Complete!</h1>
            <div style={{ background:"#0d0e1a", border:"1px solid #1e2030", borderRadius:18, padding:22, marginBottom:18 }}>
              <div style={{ width:72, height:72, borderRadius:99, border:`3px solid ${gc}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", margin:"0 auto 14px", boxShadow:`0 0 20px ${gc}44` }}>
                <span style={{ fontSize:26, fontWeight:900, color:gc, lineHeight:1 }}>{grd}</span>
              </div>
              <div style={{ fontSize:22, fontWeight:900 }}>{qScore}/{quizQs.length} <span style={{ fontSize:14, color:"#64748b" }}>• {pct}%</span></div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:9, marginTop:14 }}>
                {[["✓", qScore, "#34D399"], ["✗", qAnswers.filter(a => !a.correct && !a.timeout).length, "#F87171"], ["⏱", qAnswers.filter(a => a.timeout).length, "#FB923C"]].map(([l, v, c]) => (
                  <div key={l} style={{ background:"#060710", borderRadius:7, padding:10 }}>
                    <div style={{ fontSize:18, fontWeight:900, color:c }}>{v}</div>
                    <div style={{ fontSize:10, color:"#475569" }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display:"flex", gap:7 }}>
              <button onClick={startQuiz}          style={{ ...S.btn(color, false), flex:1, padding:"11px" }}>Retry</button>
              <button onClick={() => setMode("notes")} style={{ ...S.btn(color, true),  flex:1, padding:"11px" }}>Notes</button>
              <button onClick={() => setMode("home")}  style={{ ...S.btn("#475569", true), flex:1, padding:"11px" }}>Home</button>
            </div>
          </div>
        </div>
      );
    }

    const q = quizQs[qIdx];
    if (!q) return null;
    return (
      <div style={S.app}>
        {aiPanel && <AIPanel {...aiPanel} color={color} apiKey={apiKey} onClose={() => setAiPanel(null)} />}
        <div style={S.nav}>
          <button onClick={() => setMode("home")} style={{ ...S.btn(color, true), padding:"5px 11px", fontSize:11 }}>← Exit</button>
          <div style={{ flex:1, display:"flex", justifyContent:"center" }}>
            <div style={{ width:160, background:"#0d0e1a", borderRadius:99, height:4, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${((qIdx + 1) / quizQs.length) * 100}%`, background:color, transition:"width 0.3s" }} />
            </div>
          </div>
          <div style={{ width:38, height:38, borderRadius:99, border:`2px solid ${timeLeft <= 10 ? "#F87171" : color}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:900, color: timeLeft <= 10 ? "#F87171" : color }}>
            {timeLeft}
          </div>
        </div>
        <div style={{ maxWidth:640, margin:"0 auto", padding:"22px 18px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#475569", marginBottom:12 }}>
            <span>Q{qIdx + 1}/{quizQs.length}</span><span>{qScore} correct</span>
          </div>
          <div style={{ background:"#0d0e1a", border:"1px solid #1e2030", borderRadius:13, padding:18, marginBottom:12 }}>
            <div style={{ fontSize:10, color, letterSpacing:2, marginBottom:8, textTransform:"uppercase" }}>Question</div>
            <p style={{ fontSize:15, fontWeight:600, lineHeight:1.5, margin:0 }}>{q.q}</p>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:12 }}>
            {q.opts.map((opt, i) => {
              let bg = "#0d0e1a", border = "#1e2030", c = "#94a3b8";
              if (qSel === i && !qConf)  { bg = `${color}18`; border = color; c = "#e2e8f0"; }
              if (qConf) {
                if (i === q.a)                           { bg = "#34D39918"; border = "#34D399"; c = "#34D399"; }
                else if (i === qSel && qSel !== q.a)     { bg = "#F8717118"; border = "#F87171"; c = "#F87171"; }
                else                                     { bg = "#060710";   border = "#0d0e1a"; c = "#334155"; }
              }
              return (
                <button key={i} onClick={() => !qConf && setQSel(i)} style={{ background:bg, border:`1px solid ${border}`, borderRadius:9, padding:"10px 14px", cursor: qConf ? "default" : "pointer", color:c, fontSize:12, textAlign:"left", display:"flex", gap:9, alignItems:"center", fontFamily:"inherit", transition:"all 0.15s" }}>
                  <span style={{ width:20, height:20, borderRadius:99, background: qConf && i === q.a ? "#34D399" : qConf && i === qSel && qSel !== q.a ? "#F87171" : "#1e2030", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, flexShrink:0, color: qConf && (i === q.a || i === qSel) ? "#fff" : "#475569" }}>
                    {qConf && i === q.a ? "✓" : qConf && i === qSel && qSel !== q.a ? "✗" : ["A","B","C","D"][i]}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
          {qConf && (
            <div style={{ background:"#0a1420", border:`1px solid ${color}33`, borderRadius:9, padding:"11px 14px", marginBottom:10 }}>
              <div style={{ fontSize:11, color, marginBottom:5, fontWeight:700 }}>Explanation</div>
              <div style={{ fontSize:12, color:"#94a3b8", lineHeight:1.6 }}>{q.exp}</div>
              <button onClick={() => setAiPanel({ title:"💡 Claude Explains This Question", prompt: buildQuizExplainPrompt(q, q.a, qSel, topic?.label, roleName) })}
                style={{ marginTop:8, background:"#34D39918", border:"1px solid #34D39944", borderRadius:7, padding:"7px 12px", color:"#34D399", cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"inherit" }}>
                🤖 Ask Claude for deeper explanation
              </button>
            </div>
          )}
          <button onClick={qConf ? nextQ : () => qSel !== null && confirmQ()} style={{ ...S.btn(color, false), width:"100%", padding:"12px", fontSize:13, opacity: (!qConf && qSel === null) ? 0.4 : 1, cursor: (!qConf && qSel === null) ? "not-allowed" : "pointer" }}>
            {qConf ? (qIdx + 1 >= quizQs.length ? "See Results →" : "Next →") : qSel !== null ? "Confirm Answer" : "Select an option"}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
